import { type GetPaths, useClientPortalAPI } from "@certn/api";
import { Loader, SectionMessage } from "@certn/cascadia-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  ReferenceCheckTemplatesContext,
  initialReferenceCheckTemplateValues,
} from "./create/ReferenceCheckTemplatesContext";
import { ReferenceCheckTemplateDetailsStep } from "./create/steps/ReferenceCheckTemplateDetailsStep";
import { ReferenceCheckTemplateQuestionsStep } from "./create/steps/ReferenceCheckTemplateQuestionsStep";
import { useSteps } from "../../hooks/useSteps";
import type { ReferenceCheckTemplateFormValues } from "./utils";

interface DefaultQuestionsResponse {
  professional_quantity?: number;
  personal_quantity?: number;
  professional_questions?: string[];
  personal_questions?: string[];
}

function WizardProvider({
  initialTemplate,
}: {
  initialTemplate: ReferenceCheckTemplateFormValues;
}) {
  const { currentStep, handleNext, handlePrev } = useSteps(0);
  const [template, setTemplate] =
    useState<ReferenceCheckTemplateFormValues>(initialTemplate);

  const steps = [
    <ReferenceCheckTemplateDetailsStep key="details" />,
    <ReferenceCheckTemplateQuestionsStep key="questions" />,
  ];

  return (
    <ReferenceCheckTemplatesContext.Provider
      value={{
        template,
        setTemplate,
        isLastStep: currentStep >= steps.length - 1,
        numberOfSteps: steps.length,
        currentStep,
        goToNextStep: handleNext,
        goToPrevStep: handlePrev,
      }}
    >
      {steps[currentStep]}
    </ReferenceCheckTemplatesContext.Provider>
  );
}

export function ReferenceCheckTemplateCreatePage() {
  const { t } = useTranslation();

  const {
    data: defaults,
    isLoading: loadingDefaults,
    error: defaultsError,
  } = useClientPortalAPI(
    "/api/internal/client-portal/check-arguments-templates/reference/default-questions/" as GetPaths
  );

  if (loadingDefaults) {
    return <Loader overlay />;
  }

  if (defaultsError) {
    return (
      <SectionMessage type="danger">
        {t("Something went wrong fetching the list of questions")}
      </SectionMessage>
    );
  }

  const defaultData = defaults as unknown as DefaultQuestionsResponse | null;

  const initialTemplate: ReferenceCheckTemplateFormValues = {
    ...initialReferenceCheckTemplateValues,
    professional_quantity:
      defaultData?.professional_quantity ??
      initialReferenceCheckTemplateValues.professional_quantity,
    personal_quantity:
      defaultData?.personal_quantity ??
      initialReferenceCheckTemplateValues.personal_quantity,
    professional_questions: defaultData?.professional_questions?.length
      ? defaultData.professional_questions
      : [""],
    personal_questions: defaultData?.personal_questions?.length
      ? defaultData.personal_questions
      : [""],
  };

  return <WizardProvider initialTemplate={initialTemplate} />;
}
